/* eslint-disable react/no-multi-comp */
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

import {
    CalculatorStepAccordion,
    QuestionMultiInput,
    Question,
    FormComputed,
    HiddenField,
    MessagePropType,
    useForm,
    useCalculator,
    useIsMounted,
    LegacyCityPropType,
    PROVINCE,
} from '@ratehub/base-ui';
import {
    fetchQualifyingRate,
    SCENARIO_TYPES,
    DEFAULT_ALL_PURCHASE_PARAMS,
    MORTGAGE_PLACEHOLDERS,
    GREATER_THAN_ZERO_VALIDATOR,
} from '@ratehub/mtg-common';
import {
    can_mortgage_affordability,
    prop_tax,
    utilities_estimate,
} from '@ratehub/mortgage-calculations';

import {
    ANNUAL_INCOME_FIELDS,
    LIVING_COSTS_FIELDS,
    DEBT_PAYMENT_FIELDS,
    ANNUAL_INCOME_FIELD_NAMES,
    LIVING_COSTS_FIELD_NAMES,
    DEBT_PAYMENT_FIELD_NAMES,
} from '../definitions/AffordabilityMultiInputFields';
import useMortgageRecommendedRate from '../hooks/useMortgageRecommendedRate';


const DEFAULT_QUALIFYING_RATE = 5.25;

function AffordabilityStepInputs({
    title,
    ctaText,
    ctaDataName,
    stepNumber,
    isInitiallyExpanded,
    initialValues,
    onToggleExpand,
    onConfirmComplete,
    accordionDataName,
    ...otherProps
}) {
    const form = useForm();
    const recommendedRateState = useMortgageRecommendedRate();
    const getIsMounted = useIsMounted();

    const calculatorState = useCalculator();
    const document = calculatorState.document;

    const {
        annualIncomeFieldValues,
        cityFieldValue,
        livingCostsFieldValues,
        debtPaymentFieldValues,
    } = getSavedFormValues(toJS(document?.journey?.calculators), initialValues);

    useEffect(() => {
        // Fetch the qualifying and contract rates on mount and store it in Form

        // Qualifying rate
        fetchQualifyingRate()
            .then(({ data }) => {
                if (!getIsMounted()) {
                    return;
                }

                form
                    .getFieldNamed('qualifyingRate')
                    .setValue(data.qualifyingRate);
            })
            // Default to current qualifying rate if the API is down for whatever reason
            // Issue will be created to handle fetching as part of getServerProps or something in the future.
            .catch(() => {
                if (!getIsMounted()) {
                    return;
                }

                form.getFieldNamed('qualifyingRate').setValue(DEFAULT_QUALIFYING_RATE);
            });

        // Contract rate
        recommendedRateState
            .fetchRecommendedRate({
                scenario: SCENARIO_TYPES.PURCHASE,
                ...DEFAULT_ALL_PURCHASE_PARAMS,
            })
            .then(() => {
                if (!getIsMounted()) {
                    return;
                }

                form
                    .getFieldNamed('recommendedRate')
                    .setValue(recommendedRateState.recommendedRate);
            });
    }, []);

    return (
        <Container {...otherProps}>
            <FormComputed
                name="maxAffordabilityScenario"
                value={calculateMaxAffordability}
            />

            <FormComputed
                name="maxAffordability"
                value={getMaxHomePrice}
            />

            <HiddenField
                name="qualifyingRate"
            />

            <HiddenField
                name="recommendedRate"
            />

            <CalculatorStepAccordion
                title={title}
                ctaText={ctaText}
                ctaDataName={ctaDataName}
                value="maxAffordability"
                valueFormat="CAD0"
                showBadge
                stepNumber={stepNumber}
                isInitiallyExpanded={isInitiallyExpanded}
                onToggleExpand={onToggleExpand}
                hasCompleteButton
                onConfirmComplete={(currentStepNumber, formValues) => onConfirmComplete?.({ stepNumber: currentStepNumber, formValues, document })}
                accordionDataName={accordionDataName}
                messageBannerProps={
                    calculatorState.hasCompletedPreviousStep(2)
                        ? {
                            variant: 'success',
                            message: (
                                <FormattedMessage
                                    id="mtg-calculators.AffordabilityStepInputCollapse.messageBannerMessage"
                                    defaultMessage="Affordability is estimated based on income,
                                debt, down payment, and interest rate using industry-recognized lending ratios."
                                />
                            ),
                            iconKey: 'checkmark',
                        }
                        : undefined
                }
            >
                <QuestionMultiInput
                    fields={getFieldsWithInitialValues(ANNUAL_INCOME_FIELDS, annualIncomeFieldValues)}
                    hideLabels
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.annualIncomeLabel"
                            defaultMessage="Annual income"
                        />
                    )}
                    quicktipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.annualIncomeQuicktip"
                            defaultMessage="Your gross income before-tax, including any bonuses and supplementary income."
                        />
                    )}
                    isSideBySide={true}
                />

                <Question
                    field={{
                        name: 'estimatedDownPayment',
                        type: 'integer',
                        validators: [ GREATER_THAN_ZERO_VALIDATOR ],
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.downPaymentLabel"
                            defaultMessage="Down payment"
                        />
                    )}
                    placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                    tooltipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.downPaymentTooltip"
                            defaultMessage="The amount of money you pay up front to obtain a mortgage.
                                The minimum down payment in Canada is 5%. For down payments of less than 20%,
                                home buyers are required to purchase mortgage default insurance, commonly referred to as CMHC insurance."
                        />
                    )}
                    type="currency"
                    isSideBySide={true}
                />

                <Question
                    field={{
                        name: 'futureHomeLocation',
                        type: 'city',
                        value: cityFieldValue,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.homeLocationLabel"
                            defaultMessage="Location of your future home"
                        />
                    )}
                    placeholder={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.locationPlaceholder"
                            defaultMessage="City"
                        />
                    )}
                    isSideBySide={true}
                />

                <QuestionMultiInput
                    fields={getFieldsWithInitialValues(LIVING_COSTS_FIELDS, livingCostsFieldValues)}
                    hideLabels
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.livingCostsLabel"
                            defaultMessage="Living costs of your future home"
                        />
                    )}
                    quicktipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.livingCostsQuicktip"
                            defaultMessage="If you don't know these costs, leave the fields blank and we will estimate for you."
                        />
                    )}
                    tooltipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.livingCostsTooltip"
                            defaultMessage="<strong>Property tax</strong>
                                An annual tax levied by the municipality where your home is located.

                                <p>
                                    <strong>Condo fees</strong>
                                    Condo fees cover common building expenses and maintenance in multi-unit properties.
                                </p>

                                <strong>Heating costs</strong>
                                The total costs associated with heating your home and water for your home."
                            values={{
                                strong: chunks => (
                                    <strong className="tooltipHeading">
                                        {chunks}
                                    </strong>
                                ),
                            }}
                        />
                    )}
                    isSideBySide={true}
                />

                <QuestionMultiInput
                    fields={getFieldsWithInitialValues(DEBT_PAYMENT_FIELDS, debtPaymentFieldValues)}
                    hideLabels
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.debtPaymentLabel"
                            defaultMessage="Debt payments"
                        />
                    )}
                    quicktipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.debtPaymentsQuicktip"
                            defaultMessage="Enter debt payments if applicable. If you have none, you can leave blank."
                        />
                    )}
                    tooltipMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepInputs.debtPaymentTooltip"
                            defaultMessage="<strong>Credit card debt payment</strong>
                                The average monthly interest owed on outstanding credit card balances.
                                This does not include the balances themselves, only the interest portion.

                                <p>
                                    <strong>Car payment</strong>
                                    Your monthly leasing or financing on any household vehicles.
                                </p>

                                <strong>Loan expenses</strong>
                                Any other monthly loan expenses, such as lines of credit, student loans, alimony or child support."
                            values={{
                                strong: chunks => (
                                    <strong className="tooltipHeading">
                                        {chunks}
                                    </strong>
                                ),
                            }}
                        />
                    )}
                    isSideBySide={true}
                />
            </CalculatorStepAccordion>
        </Container>
    );
}

AffordabilityStepInputs.propTypes = {
    title: MessagePropType.isRequired,
    ctaText: MessagePropType.isRequired,
    ctaDataName: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,

    isInitiallyExpanded: PropTypes.bool,
    initialValues: PropTypes.shape({
        city: LegacyCityPropType,
        province: PropTypes.oneOf(Object.values(PROVINCE)),
        yourIncome: PropTypes.number,
        coApplicantIncome: PropTypes.number,
        downPayment: PropTypes.number,
        propertyTax: PropTypes.number,
        condoFees: PropTypes.number,
        monthlyHeat: PropTypes.number,
        creditCardPayment: PropTypes.number,
        carPayment: PropTypes.number,
        otherExpenses: PropTypes.number,
    }),

    onToggleExpand: PropTypes.func,
    onConfirmComplete: PropTypes.func,
    accordionDataName: PropTypes.string.isRequired,
};

AffordabilityStepInputs.defaultProps = {
    isInitiallyExpanded: undefined,
    initialValues: {},
    onToggleExpand: undefined,
    onConfirmComplete: undefined,
};

function calculateMaxAffordability({
    yourIncome,
    coApplicantIncome,
    propertyTax,
    condoFees,
    heatingCosts,
    creditCardPayment,
    carPayment,
    otherExpenses,
    estimatedDownPayment,

    futureHomeLocation,
    recommendedRate,
    qualifyingRate,
}) {
    const contractRate = recommendedRate?.value / 100;
    qualifyingRate /= 100; // Ensure we're dealing with the correct format of a rate

    const years = 25; // 25 year amortization

    // Gross combined income
    const annualIncome = (yourIncome ?? 0) + (coApplicantIncome ?? 0);

    // Gross combined living expenses
    let livingExpenses = ((propertyTax && propertyTax / 12) ?? 0)
        + (condoFees ?? 0)
        + (heatingCosts ?? 0);

    // Gross combined debt expenses
    const debtExpenses = (creditCardPayment ?? 0) + (carPayment ?? 0) + (otherExpenses ?? 0);

    let affordability = can_mortgage_affordability(
        contractRate,
        qualifyingRate,
        years,
        estimatedDownPayment,
        annualIncome,
        livingExpenses,
        debtExpenses,
    );

    if (!affordability) {
        return null;
    }

    // Largely lifted from the existing calculator
    if (!propertyTax || !heatingCosts) {
        let adjustedAffordability = null;
        let previousDifference = Number.MAX_VALUE;

        while (
            adjustedAffordability == null
            || (adjustedAffordability?.mortgage > 0
                && Math.abs(
                    adjustedAffordability?.home_price - affordability.home_price,
                ) > 10)
        ) {
            if (adjustedAffordability == null) {
                adjustedAffordability = affordability;
            }

            // Handle property tax and utilities estimations
            if (!propertyTax && futureHomeLocation) {
                const { slug, province } = futureHomeLocation;

                livingExpenses -= propertyTax;
                // This is converting to monthly property tax, but we're dealing with annual. Is this affecting the affordability calculation at all?
                propertyTax = (
                    (prop_tax(province.code, slug)
                        * adjustedAffordability?.home_price)
                    / 12
                ).round(4);
                livingExpenses += propertyTax;
            }

            if (!heatingCosts) {
                livingExpenses -= heatingCosts;
                heatingCosts = utilities_estimate(
                    adjustedAffordability?.home_price,
                    heatingCosts,
                );
                livingExpenses += heatingCosts;
            }

            affordability = adjustedAffordability;

            adjustedAffordability = can_mortgage_affordability(
                contractRate,
                qualifyingRate,
                years,
                estimatedDownPayment,
                annualIncome,
                livingExpenses,
                debtExpenses,
            );

            //break infinite loop when we don't have adjustedAffordability, and use latest affordability
            if (!adjustedAffordability) {
                break;
            }

            let difference
                = adjustedAffordability?.home_price - affordability?.home_price;

            if (Math.abs(difference) < previousDifference) {
                previousDifference = Math.abs(difference);
            } else {
                affordability
                    = difference >= 0 ? adjustedAffordability : affordability;
            }
        }

        adjustedAffordability = null;
    }

    return affordability;
}

function getMaxHomePrice({ maxAffordabilityScenario }) {
    // To keep computed affordability and fixed currency input in sync, we floor the affordability
    // as to also not provide a value more than the calculated amount (ceil or round)
    return maxAffordabilityScenario
        ? Math.floor(maxAffordabilityScenario.home_price)
        : 0;
}

function getSavedFormValues(savedCalculatorData, initialValues) {
    const property = savedCalculatorData?.property;
    const cityName = property?.address?.city ?? initialValues?.city?.name;
    const provinceCode = property?.address?.province ?? initialValues?.province;

    const contacts = savedCalculatorData?.contacts;
    const primaryContact = contacts?.[0];
    const primaryContactExpenses = primaryContact?.expenses;

    return {
        annualIncomeFieldValues: {
            [ANNUAL_INCOME_FIELD_NAMES.APPLICANT_INCOME]: primaryContact?.employment?.[0]?.income ?? initialValues?.yourIncome,
            [ANNUAL_INCOME_FIELD_NAMES.CO_APPLICANT_INCOME]: contacts?.[1]?.employment?.[0]?.income ?? initialValues?.coApplicantIncome,
        },

        downPaymentFieldValue: savedCalculatorData?.mortgage?.downPayment ?? initialValues?.downPayment,

        cityFieldValue: (cityName && provinceCode) && {
            name: cityName,
            province: { code: provinceCode },
        },

        livingCostsFieldValues: {
            [LIVING_COSTS_FIELD_NAMES.PROPERTY_TAX]: property?.propertyTax ?? initialValues?.propertyTax,
            [LIVING_COSTS_FIELD_NAMES.CONDO_FEES]: property?.condoFees ?? initialValues?.condoFees,
            [LIVING_COSTS_FIELD_NAMES.HEATING_COSTS]: property?.monthlyHeat ?? initialValues?.monthlyHeat,
        },

        debtPaymentFieldValues: {
            [DEBT_PAYMENT_FIELD_NAMES.CREDIT_CARD]: primaryContactExpenses?.[0]?.value ?? initialValues?.creditCardPayment,
            [DEBT_PAYMENT_FIELD_NAMES.CAR]: primaryContactExpenses?.[1]?.value ?? initialValues?.carPayment,
            [DEBT_PAYMENT_FIELD_NAMES.OTHER]: primaryContactExpenses?.[2]?.value ?? initialValues?.otherExpenses,
        },
    };
}

function getFieldsWithInitialValues(entries, values) {
    if (!values) {
        return entries;
    }

    return entries.reduce((result, entry) => {
        if (values[entry.field.name]) {
            entry = {
                ...entry,
                field: {
                    ...entry.field,
                    value: values[entry.field.name],
                },
            };
        }

        return [
            ...result,
            entry,
        ];
    }, []);
}

const Container = styled.section`
    .tooltipHeading {
        display: block;
    }
`;

export default observer(AffordabilityStepInputs);
